import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { forkJoin } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageSearchPanelComponent } from '../../../shared/page-layout/page-search-panel.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { Mobilization } from '../models/mobilization.model';
import { MobilizationService } from '../mobilization.service';

@Component({
    selector: 'app-mobilization-change-requests',
    standalone: true,
    templateUrl: './mobilization-change-requests.component.html',
    styleUrls: ['./mobilization-change-requests.component.css'],
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        IftaLabelModule,
        InputGroupAddonModule,
        InputGroupModule,
        InputTextModule,
        TableModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageHeroComponent,
        PageSearchPanelComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class MobilizationChangeRequestsComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    keyword = '';
    submitted: Mobilization[] = [];
    rejected: Mobilization[] = [];
    isLoading = false;

    constructor(
        private mobilizationService: MobilizationService,
        private sessionStorageService: SessionStorageService,
        private messageService: MessageService,
        private router: Router
    ) {}

    ngOnInit(): void {
        this.breadcrumbItems = [{ label: 'Fleet', routerLink: '/fleet' }, { label: 'Mobilization', routerLink: '/fleet/mobilization' }, { label: 'Approvals' }];
        this.loadQueue();
    }

    loadQueue(): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Company missing', detail: 'No owning company is available in session.' });
            return;
        }

        this.isLoading = true;
        forkJoin({
            submitted: this.mobilizationService.searchMobilizations({ owningCompanyId: companyId, page: 0, pageSize: 100, status: 'SUBMITTED', keyword: this.keyword }),
            rejected: this.mobilizationService.searchMobilizations({ owningCompanyId: companyId, page: 0, pageSize: 100, status: 'REJECTED', keyword: this.keyword })
        }).subscribe({
            next: result => {
                this.submitted = result.submitted.items ?? [];
                this.rejected = result.rejected.items ?? [];
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load approval queue.' });
            }
        });
    }

    clearSearch(): void {
        this.keyword = '';
        this.loadQueue();
    }

    metrics(): PageMetric[] {
        return [
            { label: 'Submitted', value: this.submitted.length, hint: 'Ready for approval decision', tone: 'warn' },
            { label: 'Rejected', value: this.rejected.length, hint: 'Can be corrected and resubmitted', tone: 'danger' },
            { label: 'Queue Total', value: this.submitted.length + this.rejected.length, hint: 'Approval-relevant assignments', tone: 'info' }
        ];
    }

    openRequest(row: Mobilization): void {
        if (row.mobilizationId) {
            this.router.navigate(['/fleet/mobilization', row.mobilizationId]);
        }
    }

    projectLabel(row: Mobilization): string {
        return row.projectSource === 'LOGISTICS_PROJECT'
            ? (row.logisticsProjectId ? `Logistics Project #${row.logisticsProjectId}` : 'Logistics Project')
            : [row.manualProjectCode, row.manualProjectName].filter(Boolean).join(' - ') || 'Manual project';
    }

    trailerLabel(row: Mobilization): string {
        return row.trailerAssetNumber || row.trailerPlateNumber || 'No trailer';
    }

    salaryLabel(row: Mobilization): string {
        if (row.salaryCalculationStatus === 'NO_RULE') {
            return 'No rule';
        }
        if (row.salaryTotalAmount === null || row.salaryTotalAmount === undefined) {
            return '-';
        }
        return `${row.salaryTotalAmount} ${row.salaryCurrencyCode || 'SAR'}`;
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    statusSeverity(status?: string): 'warning' | 'danger' | 'info' {
        if (status === 'REJECTED') {
            return 'danger';
        }
        return status === 'SUBMITTED' ? 'warning' : 'info';
    }

    activeFilterCount(): number {
        return this.keyword.trim() ? 1 : 0;
    }
}
